import React, { useRef, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Upload, Loader2, X, Image as ImageIcon } from "lucide-react";
import { storage } from "@/firebase/config";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useToast } from "@/components/ui/use-toast";

interface ImageUploaderProps {
    value: string;
    onChange: (url: string) => void;
    label?: string;
    recommendedSize?: string;
    className?: string;
    subdomain?: string;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({ 
    value, 
    onChange, 
    label = "Upload Image", 
    recommendedSize,
    className,
    subdomain
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const { toast } = useToast();

    const handleImageUpload = async (file: File) => {
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast({ title: "Invalid file", description: "Please upload an image file.", variant: "destructive" });
            return;
        }

        // Validate file size (e.g., max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast({ title: "File too large", description: "Image must be less than 5MB.", variant: "destructive" });
            return;
        }

        setIsUploading(true);
        try {
            // Generate unique path
            const timestamp = Date.now();
            const randomId = Math.random().toString(36).substring(7);
            // Use school-specific path if subdomain is available
            const basePath = subdomain ? `schools/${subdomain}/uploads` : `schools/uploads`;
            const path = `${basePath}/${timestamp}_${randomId}_${file.name}`;
            const storageRef = ref(storage, path);

            // Upload
            const snapshot = await uploadBytes(storageRef, file);
            const url = await getDownloadURL(snapshot.ref);

            onChange(url);
            toast({ title: "Success", description: "Image uploaded successfully." });
        } catch (error) {
            console.error("Upload failed", error);
            toast({ title: "Upload Failed", description: "Could not upload image. Try again.", variant: "destructive" });
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className={`space-y-3 ${className}`}>
            {label && (
                 <Label className="text-slate-700 dark:text-slate-300 font-medium flex items-center gap-1">
                    {label}
                </Label>
            )}
            
            {/* Preview Area */}
            <div className="relative w-full h-48 bg-slate-50 dark:bg-slate-900 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700 overflow-hidden group">
                {value ? (
                    <>
                        <img src={value} alt="Preview" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-3 p-4">
                            <p className="text-white text-xs font-medium">Current Image</p>
                            <div className="flex gap-2">
                                <Button size="sm" variant="secondary" onClick={() => fileInputRef.current?.click()}>
                                    Replace
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => onChange('')}>
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div 
                        className="h-full flex flex-col items-center justify-center cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {isUploading ? (
                            <div className="text-center">
                                <Loader2 className="h-8 w-8 animate-spin text-purple-600 mx-auto mb-2" />
                                <span className="text-xs text-slate-500">Uploading...</span>
                            </div>
                        ) : (
                            <div className="text-center p-4">
                                <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                                <span className="text-sm font-medium text-slate-600 block">Click to upload image</span>
                                {recommendedSize && <span className="text-xs text-slate-400 mt-1 block">{recommendedSize}</span>}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept="image/*"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file);
                }}
            />

            {/* URL Input Fallback */}
            <div className="flex gap-2">
                <Input 
                    value={value || ''} 
                    onChange={(e) => onChange(e.target.value)} 
                    className="flex-1 font-mono text-xs h-9"
                    placeholder="Or paste image URL..." 
                />
                <Button 
                    variant="outline" 
                    size="sm"
                    className="h-9 px-3"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                >
                    {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                </Button>
            </div>
        </div>
    );
};
